import validators from '@/helpers/validators';
import useForm from '@/hooks/useForm';
import React, { JSX, useEffect, useState } from 'react';

import { FormInputNameCreditCard } from '@/features/profile/types';
import { onlyNumbers } from '@/helpers/common';
import { encrypt } from '@/helpers/crypt/crypt';
import {
  getCreditCardLabel,
  updateHolderName,
  updateMaskCard,
  updateMaskCVV,
  validateCVV,
} from '@/helpers/masks/cardNumber';
import { updateMask as cpfCnpjMask } from '@/helpers/masks/cpfCnpj';
import { updateMask as expirationDateMask } from '@/helpers/masks/expirationDate';
import { useDialog } from '@/hooks/useDialog';
import Card from '@/model/Card';
import CardClient from '@/model/CardClient';
import CardClientCrypt from '@/model/CardClientCrypt';
import { setLoading } from '@/redux/loading/loadingSlice';
import { api } from '@/services/api';
import { UseFormControl } from '@/types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCardValidation } from '@/redux/cardValidation/cardValidationSlice';
import { CreditCardParameters } from '@/model/CreditCardParameters';
import { CheckCardWillValidateRequest } from '@/model/CheckCardWillValidateRequest';
import { CheckCardWillValidateResponse } from '@/model/CheckCardWillValidateResponse';
import { AddCreditCardUI } from './ui';
// import Client from '@/model/Client';

export const AddCreditCardScreen: React.FC = (): JSX.Element => {
  const [willValidate, setWillValidate] = useState<boolean>(true);
  const { creditCardParameters } = useSelector(useCardValidation);

  const dispatch = useDispatch();
  const history = useNavigate();

  const { onSetVisible } = useDialog();

  const handleOnCheckIfWillBeValidated = async (parameter: CreditCardParameters): Promise<void> => {
    if (!parameter.openOnProfile && parameter.events && parameter.events.length > 0) {
      try {
        dispatch(setLoading(true));
        const payload: CheckCardWillValidateRequest = {
          events: parameter.events,
        };
        const { data } = await api.post<CheckCardWillValidateResponse>(
          '/client/my-account/card/check-validate',
          payload,
        );
        setWillValidate(data.validate);
        dispatch(setLoading(false));
      } catch {
        dispatch(setLoading(false));
      }
    } else {
      setWillValidate(false);
    }
  };

  //   const getUserData = async (): Promise<void> => {
  //     const response = await api.get<Client>('client/my-account');
  //     setClient(response.data);
  //   };

  const {
    formData: formDataAddCreditCard,
    formErrors: formErrorsAddCreditCard,
    setErrors: setErrorsAddCreditCard,
    onChangeFormInput: onChangeFormInputAddCreditCard,
    isFormValid: isFormValidAddCreditCard,
    resetForm: resetFormAddCreditCard,
  } = useForm({
    initialData: {
      number: '',
      month: '',
      year: '',
      cvv: '',
      name: '',
      document: '',
    },
    validators: {
      number: [validators.required, validators.cardNumber],
      month: [validators.required],
      year: [validators.required],
      cvv: [validators.required, validators.cvv],
      name: [validators.required],
      document: [validators.required, validators.cpforcnpj],
    },
    formatters: {
      number: updateMaskCard,
      date: expirationDateMask,
      cvv: updateMaskCVV,
      name: updateHolderName,
      document: cpfCnpjMask,
    },
  });

  const onSubmitAddCreditCard = async (): Promise<void> => {
    if (isFormValidAddCreditCard()) {
      if (
        validateCVV(
          formDataAddCreditCard[FormInputNameCreditCard.number],
          formDataAddCreditCard[FormInputNameCreditCard.cvv],
        )
      ) {
        try {
          dispatch(setLoading(true));
          const card: Card = {
            holder: formDataAddCreditCard[FormInputNameCreditCard.name],
            number: onlyNumbers(formDataAddCreditCard[FormInputNameCreditCard.number]),
            expMonth: formDataAddCreditCard[FormInputNameCreditCard.month],
            expYear: formDataAddCreditCard[FormInputNameCreditCard.year],
            securityCode: formDataAddCreditCard[FormInputNameCreditCard.cvv],
            document: onlyNumbers(formDataAddCreditCard[FormInputNameCreditCard.document]),
            cardType: getCreditCardLabel(formDataAddCreditCard[FormInputNameCreditCard.number]),
          };
          const encrypted = encrypt(JSON.stringify(card));

          const cardClientCrypt: CardClientCrypt = {
            data: encrypted,
          };
          const response = await api.post<CardClient>('client/my-account/card', cardClientCrypt);

          resetFormAddCreditCard();
          onSetVisible(false);
          dispatch(setLoading(false));
          toast.success('Cartão adicionado com sucesso!');
          if (willValidate) {
            history(`/validated-card/${response.data.id}`);
          } else if (!creditCardParameters.openOnProfile) {
            history('/checkout');
          } else {
            history('/profile');
          }
        } catch {
          dispatch(setLoading(false));
        }
      } else {
        toast.warn('Código de segurança inválido!');
      }
    }
  };

  const controllerAddCreditCard: UseFormControl = {
    formData: formDataAddCreditCard,
    formErrors: formErrorsAddCreditCard,
    setErrors: setErrorsAddCreditCard,
    onChangeFormInput: onChangeFormInputAddCreditCard,
    isFormValid: isFormValidAddCreditCard,
    resetForm: resetFormAddCreditCard,
  };

  useEffect(() => {
    handleOnCheckIfWillBeValidated(creditCardParameters);
  }, [creditCardParameters]);

  return (
    <AddCreditCardUI
      willValidate={willValidate}
      controllerAddCreditCard={controllerAddCreditCard}
      onSubmit={onSubmitAddCreditCard}
    />
  );
};
